"use client";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import {
    Mail,
    Mailbox,
    MapPin,
    Phone,
    type LucideIcon,
} from "lucide-react";
import Link from "next/link";
import type { FC, SVGProps } from "react";

import InstagramIcon from "@v2/assets/icons/instagram.svg";
import TelegramIcon from "@v2/assets/icons/telegram.svg";
import WhatsappIcon from "@v2/assets/icons/whatsapp.svg";
import YoutubeIcon from "@v2/assets/icons/youtube.svg";
import FooterLogo from "@v2/assets/images/slogan.svg";
import { trackContactClick } from "@v2/lib/analytics";
import Enemad from "./enemad";

const FOOTER_LINKS = [
    { href: "/fa/about-us", label: "درباره ما" },
    { href: "tel:02144042260", label: "تماس با ما" },
    { href: "/fa/pages/privacy", label: "حریم خصوصی" },
    { href: "/fa/pages/terms-and-conditions", label: "قوانین و مقررات" },
    { href: "/fa/faq", label: "سوالات متداول" },
] as const;

type ContactItem = {
    icon: LucideIcon;
    iconColor: string;
    iconBackground: string;
    label: string;
    value: string;
    href?: string;
    trackingChannel?: "phone";
};

const CONTACT_ITEMS: ContactItem[] = [
    {
        icon: MapPin,
        iconColor: "#FFFFFF",
        iconBackground: "#CC2941",
        label: "نشانی فروشگاه",
        value: "تهران، فلکه دوم صادقیه، بلوار آیت‌الله کاشانی، بعد از بلوار اباذر، نبش گلستان شمالی، پلاک ۷۰",
    },
    {
        icon: Phone,
        iconColor: "#FFFFFF",
        iconBackground: "#CC2941",
        label: "شماره تماس",
        value: "۰۲۱-۴۴۰۴۲۲۶۰",
        href: "tel:+982144042260",
        trackingChannel: "phone",
    },
    {
        icon: Mailbox,
        iconColor: "#FFFFFF",
        iconBackground: "#CC2941",
        label: "کد پستی",
        value: "۱۴۸۱۸۷۴۵۶۱",
    },
    {
        icon: Mail,
        iconColor: "#FFFFFF",
        iconBackground: "#CC2941",
        label: "ایمیل",
        value: "vahdat.optic@gmail.com",
        href: "mailto:vahdat.optic@gmail.com",
    },
];

type SocialLink = {
    href: string;
    label: string;
    icon: FC<SVGProps<SVGSVGElement>>;
    color: string;
    hoverColor: string;
    trackingChannel?: "whatsapp";
};

const SOCIAL_LINKS: SocialLink[] = [
    {
        href: "https://www.instagram.com/vahdatoptic/",
        label: "اینستاگرام عینک وحدت",
        icon: InstagramIcon,
        color: "#FFFFFF",
        hoverColor: "#CC2941",
    },
    {
        href: "https://t.me/Vahdatoptic1",
        label: "تلگرام عینک وحدت",
        icon: TelegramIcon,
        color: "#FFFFFF",
        hoverColor: "#CC2941",
    },
    {
        href: "https://wa.me/989104443316",
        label: "واتساپ عینک وحدت",
        icon: WhatsappIcon,
        color: "#FFFFFF",
        hoverColor: "#CC2941",
        trackingChannel: "whatsapp",
    },
    {
        href: "https://www.youtube.com/@vahdatoptic",
        label: "یوتیوب عینک وحدت",
        icon: YoutubeIcon,
        color: "#FFFFFF",
        hoverColor: "#CC2941",
    },
];

const sectionTitleSx = {
    mb: 2.5,
    color: "#FFFFFF",
    fontSize: { xs: 15, lg: 16 },
    fontWeight: 700,
    lineHeight: 1.6,
};

export default function FooterCsr() {
    return (
        <Box
            component="footer"
            sx={{
                // The brand page's "why vahdat" gradient, flipped: that block lifts in
                // the middle and settles dark at its foot, so the footer picks the tone
                // up again and darkens downward into the bottom bar. Both layers are
                // centred or vertical, so the rtl transform cannot mirror them.
                bgcolor: "#17191c",
                backgroundImage: "radial-gradient(120% 88% at 50% 100%, rgba(187,32,46,.22), rgba(187,32,46,0) 60%), linear-gradient(180deg, #17191c 0%, #26292e 54%, #16161a 100%)",
                color: "#B8B8B8",
            }}
        >
            <Box
                sx={{
                    width: "100%",
                    maxWidth: 1400,
                    mx: "auto",
                    px: { xs: 2, sm2: 4, lg: 5 },
                    py: { xs: 5, sm2: 6, lg: 7 },
                }}
            >
                <Grid container spacing={{ xs: 5, sm2: 4, lg: 5 }}>
                    <Grid size={{ xs: 12, sm2: 6, lg: 4 }}>
                        <Box sx={{ maxWidth: { xs: "100%", lg: 340 } }}>
                            <Box
                                component={Link}
                                href="/fa"
                                prefetch={false}
                                aria-label="صفحه اصلی عینک وحدت"
                                sx={{
                                    width: 220,
                                    height: 56,
                                    display: "block",
                                }}
                            >
                                <FooterLogo width="100%" height="100%" aria-hidden="true" />
                            </Box>
                            <Typography
                                sx={{
                                    mt: 2,
                                    color: "#A7A7A7",
                                    fontSize: { xs: 13, sm2: 14 },
                                    lineHeight: 2,
                                }}
                            >
                                فروشگاه اینترنتی عینک وحدت؛ همراه شما برای انتخاب دقیق‌تر و خرید انواع عینک.
                            </Typography>

                            <Box
                                component="nav"
                                aria-label="شبکه‌های اجتماعی"
                                sx={{ display: "flex", gap: 1.25, mt: 3 }}
                            >
                                {SOCIAL_LINKS.map(({
                                    href,
                                    label,
                                    icon: Icon,
                                    color,
                                    hoverColor,
                                    trackingChannel,
                                }) => (
                                    <Box
                                        key={href}
                                        component="a"
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={label}
                                        onClick={trackingChannel
                                            ? () => trackContactClick(trackingChannel, "footer")
                                            : undefined}
                                        sx={{
                                            width: 44,
                                            height: 44,
                                            display: "inline-flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                            color,
                                            bgcolor: "#252525",
                                            border: "1px solid #343434",
                                            borderRadius: "50%",
                                            transition: "color 180ms ease, background-color 180ms ease, transform 180ms ease",
                                            "&:hover": {
                                                color: hoverColor,
                                                bgcolor: "#2C2C2C",
                                                borderColor: "#CC2941",
                                                transform: "translateY(-2px)",
                                            },
                                            "&:focus-visible": {
                                                outline: "2px solid #FFFFFF",
                                                outlineOffset: 3,
                                            },
                                        }}
                                    >
                                        <Icon width={21} height={21} aria-hidden="true" />
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm2: 6, lg: 2 }}>
                        <Box component="nav" aria-label="دسترسی سریع">
                            <Typography component="h2" sx={sectionTitleSx}>
                                دسترسی سریع
                            </Typography>
                            <Box
                                component="ul"
                                sx={{
                                    display: "grid",
                                    gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm2: "1fr" },
                                    gap: { xs: "4px 16px", sm2: 0.5 },
                                    p: 0,
                                    m: 0,
                                    listStyle: "none",
                                }}
                            >
                                {FOOTER_LINKS.map(({ href, label }) => (
                                    <Box component="li" key={href}>
                                        <Box
                                            component={Link}
                                            href={href}
                                            prefetch={false}
                                            sx={{
                                                minHeight: 40,
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 1,
                                                color: "#B8B8B8",
                                                textDecoration: "none",
                                                fontSize: 14,
                                                lineHeight: 1.8,
                                                transition: "color 180ms ease, transform 180ms ease",
                                                "&::before": {
                                                    content: '""',
                                                    width: 5,
                                                    height: 5,
                                                    flex: "0 0 auto",
                                                    bgcolor: "#CC2941",
                                                    borderRadius: "50%",
                                                },
                                                "&:hover": {
                                                    color: "#FFFFFF",
                                                    transform: "translateX(-3px)",
                                                },
                                                "&:focus-visible": {
                                                    outline: "2px solid #FFFFFF",
                                                    outlineOffset: 2,
                                                },
                                            }}
                                        >
                                            {label}
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm2: 7, lg: 4 }}>
                        <Box component="address" sx={{ m: 0, fontStyle: "normal" }}>
                            <Typography component="h2" sx={sectionTitleSx}>
                                ارتباط با ما
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2.25 }}>
                                {CONTACT_ITEMS.map(({
                                    icon: Icon,
                                    iconColor,
                                    iconBackground,
                                    label,
                                    value,
                                    href,
                                    trackingChannel,
                                }) => (
                                    <Box
                                        key={label}
                                        component={href ? "a" : "div"}
                                        href={href}
                                        onClick={trackingChannel
                                            ? () => trackContactClick(trackingChannel, "footer")
                                            : undefined}
                                        sx={{
                                            display: "flex",
                                            alignItems: "flex-start",
                                            gap: 1.5,
                                            color: "inherit",
                                            textDecoration: "none",
                                            ...(href && {
                                                "&:hover .contact-value": { color: "#FFFFFF" },
                                                "&:focus-visible": {
                                                    outline: "2px solid #FFFFFF",
                                                    outlineOffset: 4,
                                                    borderRadius: 1,
                                                },
                                            }),
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: 38,
                                                height: 38,
                                                flex: "0 0 auto",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                bgcolor: iconBackground,
                                                borderRadius: 1.5,
                                            }}
                                        >
                                            <Icon color={iconColor} size={19} strokeWidth={1.8} />
                                        </Box>
                                        <Box sx={{ minWidth: 0, pt: 0.1 }}>
                                            <Typography sx={{ color: "#7F7F7F", fontSize: 11, lineHeight: 1.5 }}>
                                                {label}
                                            </Typography>
                                            <Typography
                                                className="contact-value"
                                                sx={{
                                                    mt: 0.35,
                                                    color: "#C7C7C7",
                                                    fontSize: { xs: 13, sm2: 14 },
                                                    lineHeight: 1.9,
                                                    overflowWrap: "anywhere",
                                                    transition: "color 180ms ease",
                                                }}
                                            >
                                                {value}
                                            </Typography>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    </Grid>

                    <Grid
                        size={{ xs: 12, sm2: 5, lg: 2 }}
                        // The badge is one more column of the footer, so it starts on the
                        // same edge as the ones beside it at every width.
                        sx={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-start" }}
                    >
                        <Box
                            sx={{
                                height: "100%",
                                width: 112,
                                maxWidth: "100%",
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "flex-start",
                            }}
                        >
                            <Typography component="h2" sx={{ ...sectionTitleSx, width: "100%" }}>
                                خرید مطمئن
                            </Typography>
                            <Box
                                sx={{
                                    width: "100%",
                                    maxWidth: "100%",
                                    minHeight: 112,
                                    boxSizing: "border-box",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: "#FFFFFF",
                                    borderRadius: 2,
                                    p: 1.5,
                                }}
                            >
                                <Enemad />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
            </Box>

            {/* Tinted rather than its own flat colour, so the gradient above keeps
                running through the strip instead of hitting a seam at its top edge. */}
            <Box sx={{ borderTop: "1px solid rgba(255,255,255,.08)", bgcolor: "rgba(0,0,0,.38)" }}>
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 1400,
                        minHeight: 64,
                        mx: "auto",
                        px: { xs: 2, sm2: 4, lg: 5 },
                        py: 2,
                        display: "flex",
                        flexDirection: { xs: "column", sm2: "row" },
                        alignItems: { xs: "flex-start", sm2: "center" },
                        justifyContent: "space-between",
                        gap: 1,
                    }}
                >
                    <Typography sx={{ color: "#8A8A8A", fontSize: 12, lineHeight: 1.8 }}>
                        تمامی حقوق برای شرکت بازرگانی اپتیک وحدت محفوظ است.
                    </Typography>
                    <Typography sx={{ color: "#666666", fontSize: 11, lineHeight: 1.8 }}>
                        طراحی و توسعه توسط واحد فناوری اطلاعات اپتیک وحدت
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
}
